import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Archive, ArchiveRestore, DownloadIcon, Upload } from "lucide-react";
import { toast } from "react-toastify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useArchiveSpecList, useDownloadMasterSpec, useSpecLists } from "@/hooks/useSpecLists";
import { SpecListMinimal } from "@/types/specList";
import Tooltip from "@/components/shared/Tooltip";
import { GenericErrorDisplay } from "@/components/shared/ErrorDisplay";
import { LoadingDisplay } from "@/components/shared/LoadingDisplay";
import { UploadMasterSpecDialog } from "@/components/masterSpecs/UploadMasterSpecDialog";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";
import { cn } from "@/lib/utils";

export function MasterSpecsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showArchived, setShowArchived] = useState(false);

  const modalType = searchParams.get("modal");
  const uploadDialogOpen = modalType === "upload";

  const {
    data: specLists,
    isLoading: isSpecListsLoading,
    error: isSpecListsError,
    mutate: refetchSpecListsData,
  } = useSpecLists();
  const { trigger: archiveSpecList, isMutating: isArchiving } = useArchiveSpecList();
  const downloadMasterSpec = useDownloadMasterSpec();

  const openUploadModal = () => {
    setSearchParams({ modal: "upload" });
  };

  const closeModal = () => {
    setSearchParams({});
  };

  const masterSpecs = specLists.filter((list) => list.type === "master");

  const filteredMasterSpecs = masterSpecs.filter((list) =>
    showArchived ? true : !list.isArchived,
  );

  const handleArchiveToggle = async (specList: SpecListMinimal) => {
    try {
      const successMessage = specList.isArchived
        ? `Successfully unarchived Master Spec ${specList.name}.`
        : `Successfully archived Master Spec ${specList.name}!`;

      await archiveSpecList(
        {
          id: specList.id,
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(successMessage);
            refetchSpecListsData();
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async (specList: SpecListMinimal) => {
    try {
      await downloadMasterSpec(specList.id);
    } catch (error) {
      console.error(error);
      toast.error("Failed to download master spec source file.");
    }
  };

  if (isSpecListsLoading) {
    return <LoadingDisplay />;
  }

  if (isSpecListsError) {
    return <GenericErrorDisplay />;
  }

  return (
    <>
      <Header />
      <Main>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Master Specs</h1>
            <p className="text-muted-foreground">Manage master specification files</p>
          </div>
          <Button onClick={openUploadModal}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Master Spec
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                Master Specs
                {filteredMasterSpecs.length > 0 && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    ({filteredMasterSpecs.length} total)
                  </span>
                )}
              </span>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-archived"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                />
                <Label htmlFor="show-archived" className="text-sm font-medium">
                  Show Archived
                </Label>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Latest Version</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMasterSpecs.map((specList) => (
                  <TableRow
                    key={specList.id}
                    className={cn(`hover:bg-muted/50 cursor-pointer`, {
                      "[&>td]:opacity-60": specList.isArchived,
                    })}
                    onClick={() =>
                      navigate(`/spec-lists/${specList.id}/version/${specList.latestVersion.id}`)
                    }
                  >
                    <TableCell className="font-medium">
                      {specList.name}
                      {specList.isArchived && (
                        <Badge variant="secondary" className="ml-2">
                          Archived
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">v{specList.latestVersion.version}</Badge>
                    </TableCell>
                    <TableCell>{specList.latestVersion.createdBy}</TableCell>
                    <TableCell>
                      {new Date(specList.latestVersion.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell
                      className="text-right opacity-100!"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-end items-center gap-2">
                        {specList.latestVersion.uploaded && (
                          <Tooltip
                            asChild
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(specList)}
                              >
                                <DownloadIcon />
                              </Button>
                            }
                            content="Download master spec file"
                          />
                        )}
                        <Tooltip
                          asChild
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleArchiveToggle(specList)}
                              disabled={isArchiving}
                            >
                              {specList.isArchived ? (
                                <ArchiveRestore className="h-4 w-4" />
                              ) : (
                                <Archive className="h-4 w-4" />
                              )}
                            </Button>
                          }
                          content={specList.isArchived ? "Unarchive" : "Archive"}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredMasterSpecs.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No master specs found. Upload a master spec file to get started.
              </div>
            )}
          </CardContent>
        </Card>

        {uploadDialogOpen && (
          <UploadMasterSpecDialog
            open
            onOpenChange={(open) => {
              if (!open) closeModal();
            }}
            onSuccess={() => refetchSpecListsData()}
          />
        )}
      </Main>
    </>
  );
}
